import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Edit } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "@/components/ui/button";

import { Form, FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { useSpecListsForSpecificMask } from "@/hooks/useSpecLists";
import { useTestLabelsByMask, useUpdateTestLabel } from "@/hooks/useTestLabels";
import { useMaskStructures } from "@/hooks/useMasks";

import { TestLabel } from "@/types/testLabel";
import { useMeasurementCapabilities } from "@/hooks/useMeasurementCapabilities";
import Tooltip from "@/components/shared/Tooltip";
import { SpecsTableWrapper } from "./SpecsSelectWrapper";
import DeviceSelectTable from "@/components/shared/DeviceSelectTable";

type EditTestLabelFormProps = {
  testLabel: TestLabel;
  masks: any[];
  maskQueryParam: string | null;
};
export function EditTestLabelForm({ testLabel, masks, maskQueryParam }: EditTestLabelFormProps) {
  const navigate = useNavigate();

  const { trigger: updateTestLabel, isMutating: isUpdatingTestLabel } = useUpdateTestLabel();

  // Determine if this test label uses structures or specs
  const usesStructures = (testLabel.linkedStructures || []).length > 0;

  const formSchema = useMemo(
    () =>
      z
        .object({
          maskId: z.string().min(1, "Field is required."),
          name: z.string().min(1, "Field is required."),
          specListId: z.string().nullable(),
          measurementCapabilityId: z.string().min(1, "Field is required."),
          description: z.string().min(1, "Field is required."),
        })
        .superRefine((data, ctx) => {
          if (!usesStructures && !data.specListId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["specListId"],
              message: "Field is required.",
            });
          }
        }),
    [usesStructures],
  );
  type FormSchema = z.infer<typeof formSchema>;

  // Find the mask that matches the query parameter, fallback to testLabel's mask
  const selectedMaskId = useMemo(() => {
    if (maskQueryParam && masks.length > 0) {
      const foundMask = masks.find((mask) => mask.id === maskQueryParam);
      return foundMask ? foundMask.id : testLabel.mask.id;
    }
    return testLabel.mask.id;
  }, [maskQueryParam, masks, testLabel.mask.id]);

  const form = useForm<FormSchema>({
    defaultValues: {
      description: testLabel.description,
      maskId: selectedMaskId,
      measurementCapabilityId: testLabel.measurementCapabilityId,
      name: testLabel.name,
      specListId: testLabel.specListId ?? undefined,
    },
    resolver: zodResolver(formSchema),
    mode: "all",
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    setValue,
  } = form;

  const { maskId, specListId, measurementCapabilityId } = useWatch({
    control,
  });

  const { data: specLists, isLoading: isSpecListsForMaskLoading } =
    useSpecListsForSpecificMask(maskId);

  const { data: testLabelsForMask = [] } = useTestLabelsByMask(maskId);

  const { data: maskWithStructures, isLoading: isMaskStructuresLoading } =
    useMaskStructures(maskId);

  const { data: measurementCapabilities, isLoading: isMeasurementCapabilitiesLoading } =
    useMeasurementCapabilities();

  const [specSelectionState, setSpecSelectionState] = useState<Record<string, boolean>>(
    usesStructures
      ? {}
      : testLabel.linkedSpecs.reduce(
          (acc, cur) => {
            acc[cur.id] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        ),
  );

  const [deviceSelectionState, setDeviceSelectionState] = useState<Record<string, boolean>>(
    usesStructures
      ? testLabel.linkedStructures.reduce(
          (acc, cur) => {
            acc[cur.id] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        )
      : {},
  );

  // Effect to update mask value when query parameter changes
  useEffect(() => {
    if (maskQueryParam && masks.length > 0) {
      const foundMask = masks.find((mask) => mask.id === maskQueryParam);
      if (foundMask && foundMask.id !== maskId) {
        setValue("maskId", foundMask.id);
        resetField("specListId");
      }
    }
  }, [maskQueryParam, masks, maskId, setValue, resetField]);

  const onChangeComponentType = () => {
    setSpecSelectionState({});
  };

  const { customStructures } = useMemo(() => {
    const custom = maskWithStructures?.structures.filter((s) => s.isCustom) ?? [];

    return {
      customStructures: custom,
    };
  }, [maskWithStructures?.structures]);

  const onInvalid = (errors: typeof form.formState.errors) => {
    console.error("Validation failed:", errors);
    toast.error(`Validation failed on fields ${Object.keys(errors).join(", ")}.`);
  };
  const specRowSelection = Object.keys(specSelectionState).filter((key) => specSelectionState[key]);
  const deviceRowSelection = Object.keys(deviceSelectionState).filter(
    (key) => deviceSelectionState[key],
  );

  const onSubmit: SubmitHandler<FormSchema> = async (data) => {
    const conflict = testLabelsForMask.some(
      (tl) => tl.id !== testLabel.id && tl.name.toLowerCase() === data.name.trim().toLowerCase(),
    );

    if (conflict) {
      form.setError("name", {
        message: "A test label with this name already exists for this mask. Try a different name.",
      });
      return;
    }

    try {
      await updateTestLabel(
        {
          description: data.description,
          id: testLabel.id,
          linked_spec_ids: specRowSelection,
          linked_structure_ids: usesStructures ? deviceRowSelection : [],
          mask_id: data.maskId,
          measurement_capability_id: data.measurementCapabilityId,
          name: data.name,
          spec_list_id: data.specListId,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(`Successfully edited Test Label: "${data.name}"!`);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  const supportedRecipes = useMemo(() => {
    const measurementCapability = measurementCapabilities.find(
      (c) => c.id === measurementCapabilityId,
    );

    if (!measurementCapability) return [];

    return measurementCapability.recipes;
  }, [measurementCapabilities, measurementCapabilityId]);

  const canSubmit = usesStructures ? deviceRowSelection.length > 0 : specRowSelection.length > 0;

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Tooltip
              asChild
              content="Go back"
              trigger={
                <Button type="button" variant="outline" size="icon" onClick={goBack}>
                  <ArrowLeft />
                </Button>
              }
              side="right"
            />
            <div>
              <h1 className="text-2xl font-bold">Edit Test Label "{testLabel.name}"</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={!canSubmit || isUpdatingTestLabel}>
              <Edit className="w-4 h-4 mr-2" />
              Save changes
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">General</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormItem>
              <FormLabel htmlFor="testLabelName">
                Name <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  id="testLabelName"
                  placeholder="Enter a Test Label name"
                  {...register("name")}
                />
              </FormControl>
              {errors.name && <FormMessage>{errors.name.message}</FormMessage>}
            </FormItem>

            <FormItem>
              <FormLabel htmlFor="description">
                Description <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  id="description"
                  placeholder="Enter a description for the Test Label"
                  {...register("description")}
                />
              </FormControl>
              {errors.description && <FormMessage>{errors.description.message}</FormMessage>}
            </FormItem>

            <FormItem>
              <FormLabel htmlFor="measurementCapabilityId">
                Measurement Capability <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Controller
                  control={control}
                  name="measurementCapabilityId"
                  render={({ field }) => (
                    <Combobox
                      disabled={
                        !maskId ||
                        (!usesStructures && !specListId) ||
                        isSpecListsForMaskLoading ||
                        isMaskStructuresLoading
                      }
                      fullWidth
                      modal
                      onChange={(value) => {
                        field.onChange(value);
                        onChangeComponentType();
                      }}
                      options={measurementCapabilities.map((capability) => ({
                        key: capability.id,
                        label: capability.name,
                        value: capability.id,
                      }))}
                      placeholder={
                        isMeasurementCapabilitiesLoading
                          ? "Loading measurement capabilities..."
                          : measurementCapabilities.length === 0
                            ? "No measurement capabilities found"
                            : "Select a measurement capability"
                      }
                      searchPlaceholder="Search measurement capability..."
                      value={field.value}
                    />
                  )}
                />
              </FormControl>
              {errors.measurementCapabilityId && (
                <FormMessage>{errors.measurementCapabilityId.message}</FormMessage>
              )}
            </FormItem>
          </CardContent>
        </Card>

        {usesStructures ? (
          <>
            {maskId && measurementCapabilityId && (
              <DeviceSelectTable
                deviceData={customStructures}
                onRowSelectionChange={setDeviceSelectionState}
                rowSelectionState={deviceSelectionState}
              />
            )}
          </>
        ) : (
          <>
            {maskId && specListId && measurementCapabilityId ? (
              <SpecsTableWrapper
                linkedSpecIds={testLabel.linkedSpecs.map((ls) => ls.id)}
                maskWithStructures={maskWithStructures}
                onRowSelectionChange={setSpecSelectionState}
                rowSelectionState={specSelectionState}
                specListId={specListId}
                specLists={specLists}
                isLoading={isSpecListsForMaskLoading || isMaskStructuresLoading}
                supportedRecipes={supportedRecipes}
              />
            ) : (
              <div>No results. Try selecting a different measurement capability.</div>
            )}
          </>
        )}
      </form>
    </Form>
  );
}
